'use client';

import { devLog } from '@/lib/logging';
import type { FormSortAnswers, FormSortEvent } from '@/types/forms';
import { pushFormEventToDataLayer } from '@/utils/dataLayer';
import EmbedFlow from '@formsort/react-embed';
import { useCallback, useRef } from 'react';
import { useWizardContext } from '../hooks/useWizardContext';
import { useWizardNavigation } from '../hooks/useWizardNavigation';
import type { StepConfig } from '../types';

interface FormsortStepWrapperProps {
  stepConfig: StepConfig;
}

// Environment variable for Formsort client label
const FORMSORT_CLIENT =
  process.env.NEXT_PUBLIC_FORMSORT_CLIENT ?? 'sollis-health';

/**
 * Wrapper component for Formsort embed steps.
 *
 * Handles:
 * - Formsort embed configuration
 * - Step completion events → merge answers into wizard state
 * - Flow finalization → advance to next wizard step
 * - Prevents Formsort redirects (wizard controls navigation)
 */
export function FormsortStepWrapper({ stepConfig }: FormsortStepWrapperProps) {
  const { dispatch } = useWizardContext();
  const { goToNextStep } = useWizardNavigation();
  const flowFinalized = useRef(false);

  /**
   * Merge Formsort answers into wizard state
   */
  const mergeAnswersIntoState = useCallback(
    (answers: FormSortAnswers | undefined) => {
      if (!answers) return;

      dispatch({
        type: 'SET_FORM_DATA',
        payload: {
          // Map FormSort contact_details to our format if present
          contact_details: answers.contact_details
            ? {
                first_name: answers.contact_details.first_name,
                last_name: answers.contact_details.last_name,
                email: answers.contact_details.email_address,
                phone: answers.contact_details.phone_number,
                postal_code: answers.contact_details.postal_code,
                marketing_consent: answers.contact_details.marketing_consent,
                hdyhau: answers.contact_details.hdyhau || '',
                hdyhau_other: answers.contact_details.hdyhau_other,
              }
            : undefined,
          date_of_birth: answers.date_of_birth,
          zip_code: answers.zip_code,
          members: answers.members?.map((m, index) => ({
            id: crypto.randomUUID(),
            contact_id: crypto.randomUUID(),
            first_name: m.first_name,
            last_name: m.last_name,
            date_of_birth: m.date_of_birth,
            isPrimary: index === 0,
          })),
          visited_locations: answers.visited_locations,
          healthcare_priorities: answers.healthcare_priorities,
          concierge_healthcare_familiarity:
            answers.concierge_healthcare_familiarity,
          reference: answers.reference?.toString(),
          additional_members: answers.additional_members,
          number_of_members: answers.number_of_members,
        },
      });
    },
    [dispatch]
  );

  /**
   * Handle step completed event from Formsort
   */
  const handleStepCompleted = useCallback(
    (data: FormSortEvent) => {
      devLog('Formsort step completed:', data);

      // Merge answers into wizard state
      mergeAnswersIntoState(data.answers);

      // Push to data layer for analytics
      try {
        pushFormEventToDataLayer({
          eventName: 'StepCompleted',
          form_type: 'sign_up',
          data: {
            contact_details: data.answers?.contact_details,
            date_of_birth: data.answers?.date_of_birth,
            members: data.answers?.members || [],
            visited_locations: data.answers?.visited_locations || [],
            concierge_healthcare_familiarity:
              data.answers?.concierge_healthcare_familiarity,
            healthcare_priorities: data.answers?.healthcare_priorities || [],
            stepIndex: data.stepIndex,
          },
        });
      } catch (error) {
        console.error('Error sending data to dataLayer:', error);
      }
    },
    [mergeAnswersIntoState]
  );

  /**
   * Handle flow finalized event from Formsort
   */
  const handleFlowFinalized = useCallback(
    (data: FormSortEvent) => {
      // Prevent double handling
      if (flowFinalized.current) return;
      flowFinalized.current = true;

      devLog('Formsort flow finalized:', data);

      // Merge final answers into wizard state
      mergeAnswersIntoState(data.answers);

      // Push to data layer for analytics
      try {
        pushFormEventToDataLayer({
          eventName: 'FlowFinalized',
          form_type: 'sign_up',
          data: {
            contact_details: data.answers?.contact_details,
            date_of_birth: data.answers?.date_of_birth,
            members: data.answers?.members || [],
            visited_locations: data.answers?.visited_locations || [],
            concierge_healthcare_familiarity:
              data.answers?.concierge_healthcare_familiarity,
            healthcare_priorities: data.answers?.healthcare_priorities || [],
          },
        });
      } catch (error) {
        console.error('Error sending data to dataLayer:', error);
      }

      // Mark step as complete and navigate to next step
      dispatch({ type: 'COMPLETE_STEP', payload: stepConfig.id });

      // Small delay to ensure state is updated before navigation
      setTimeout(() => {
        goToNextStep();
      }, 100);
    },
    [mergeAnswersIntoState, dispatch, stepConfig.id, goToNextStep]
  );

  /**
   * Cancel Formsort redirects - wizard controls navigation
   */
  const handleRedirect = useCallback(() => {
    return { cancel: true };
  }, []);

  // Determine flow and variant from step config
  const flowLabel = stepConfig.formsortFlow || stepConfig.id;
  const variantLabel = stepConfig.formsortVariant || 'default';

  return (
    <div className='flex flex-1 flex-col'>
      <EmbedFlow
        clientLabel={FORMSORT_CLIENT}
        flowLabel={flowLabel}
        variantLabel={variantLabel}
        embedConfig={{
          style: {
            height: '100%',
            width: '100%',
          },
        }}
        onStepCompleted={handleStepCompleted}
        onFlowFinalized={handleFlowFinalized}
        onRedirect={handleRedirect}
      />
    </div>
  );
}
